import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EventSession, EventTicketType, OwnerEvent, clubsBookingApi } from '@/api/clubs-booking';
import AdminShell from '@/components/admin/AdminShell';

type ChildRow = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
};

type TicketRow = {
  id: string;
  ticketTypeId: string;
  sessionIds: string[];
  childSessionIds: Record<number, string[]>;
};

const createTicketRow = (ticketTypeId = ''): TicketRow => ({
  id: crypto.randomUUID(),
  ticketTypeId,
  sessionIds: [],
  childSessionIds: {},
});

const formatSessionLabel = (session: EventSession) =>
  new Date(session.startDateTime).toLocaleString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const AttendeeCreatePage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<OwnerEvent[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    eventId: '',
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    notes: '',
    children: [{ firstName: '', lastName: '', dateOfBirth: '' }] as ChildRow[],
    tickets: [createTicketRow()] as TicketRow[],
    addOnSelections: {} as Record<string, Record<number, string[]>>,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await clubsBookingApi.listMyEvents('published');
        setEvents(data.items || []);
      } catch {
        setEvents([]);
      }
    };
    void load();
  }, []);

  const selectedEvent = useMemo(
    () => events.find((event) => event._id === form.eventId) || null,
    [events, form.eventId],
  );

  const ticketTypes = useMemo(() => selectedEvent?.ticketTypes || [], [selectedEvent]);
  const eventSessions = useMemo(() => selectedEvent?.sessions || [], [selectedEvent]);
  const ticketTypeById = useMemo(
    () => new Map(ticketTypes.map((ticket) => [ticket.id, ticket])),
    [ticketTypes],
  );

  useEffect(() => {
    if (!selectedEvent) return;
    const defaultTicketTypeId = ticketTypes[0]?.id || '';
    setForm((current) => ({
      ...current,
      tickets: current.tickets.length
        ? current.tickets.map((ticket) => ({
            ...ticket,
            ticketTypeId: ticketTypes.some((entry) => entry.id === ticket.ticketTypeId)
              ? ticket.ticketTypeId
              : defaultTicketTypeId,
            sessionIds: ticket.sessionIds.filter((sessionId) =>
              eventSessions.some((session) => session.id === sessionId),
            ),
          }))
        : [createTicketRow(defaultTicketTypeId)],
    }));
  }, [selectedEvent, ticketTypes, eventSessions]);

  const getSessionsForTicket = (ticketType: EventTicketType | undefined) => {
    if (!ticketType) return [];
    if (ticketType.mode === 'all_sessions') return eventSessions;
    return eventSessions.filter((session) => ticketType.sessionIds?.includes(session.id));
  };

  const renderTicketLabel = (ticket: EventTicketType) =>
    `${ticket.name} - ${(ticket.priceCents / 100).toLocaleString('en-GB', {
      style: 'currency',
      currency: selectedEvent?.currency || 'GBP',
    })}`;

  const renderPrice = (priceCents: number) =>
    (priceCents / 100).toLocaleString('en-GB', {
      style: 'currency',
      currency: selectedEvent?.currency || 'GBP',
    });

  const cartItems = useMemo(() => {
    return form.tickets
      .flatMap((ticket) => {
        if (!ticket.ticketTypeId) return [];
        const grouped = new Map<string, { sessionIds: string[]; childIndexes: number[] }>();
        form.children.forEach((_, childIndex) => {
          const selectedSessionIds = Array.from(
            new Set((ticket.childSessionIds[childIndex] || ticket.sessionIds).filter((sessionId) => ticket.sessionIds.includes(sessionId))),
          );
          if (!selectedSessionIds.length) return;
          const key = selectedSessionIds.slice().sort().join('|');
          const existing = grouped.get(key) || { sessionIds: selectedSessionIds, childIndexes: [] };
          existing.childIndexes.push(childIndex);
          grouped.set(key, existing);
        });
        return Array.from(grouped.values()).map((group) => ({
          ticketTypeId: ticket.ticketTypeId,
          selectedSessionIds: group.sessionIds,
          childIndexes: group.childIndexes,
          quantity: 1,
        }));
      });
  }, [form.tickets, form.children]);

  const selectedSessionIdsByChild = useMemo(() => {
    const selections: Record<number, string[]> = {};
    cartItems.forEach((item) => {
      item.childIndexes.forEach((childIndex) => {
        selections[childIndex] = Array.from(new Set([
          ...(selections[childIndex] || []),
          ...item.selectedSessionIds,
        ]));
      });
    });
    return selections;
  }, [cartItems]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedEvent) {
      setError('Choose an activity');
      return;
    }

    const children = form.children
      .map((child) => ({
        firstName: child.firstName.trim(),
        lastName: child.lastName.trim(),
        dateOfBirth: child.dateOfBirth,
      }))
      .filter((child) => child.firstName || child.lastName || child.dateOfBirth);

    if (!children.length || children.some((child) => !child.firstName || !child.lastName || !child.dateOfBirth)) {
      setError('Add at least one attendee with first name, last name, and date of birth');
      return;
    }

    if (!cartItems.length || cartItems.some((ticket) => ticket.selectedSessionIds.length === 0 || ticket.childIndexes.length === 0)) {
      setError('Add at least one ticket and choose one or more sessions for each ticket');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await clubsBookingApi.createPublicBooking(selectedEvent._id, {
        parentName: form.parentName,
        parentEmail: form.parentEmail,
        parentPhone: form.parentPhone || undefined,
        children,
        checkoutMode: 'guest',
        paymentMethod: 'cash',
        cartItems: cartItems.map((item) => ({
          ...item,
          addOnSelections: Object.entries(form.addOnSelections)
            .flatMap(([addOnId, childSelections]) => Object.entries(childSelections).map(([childIndex, sessionIds]) => ({
              addOnId,
              childIndexes: [Number(childIndex)],
              sessionIds: (sessionIds || []).filter((sessionId) => item.selectedSessionIds.includes(sessionId)),
            })))
            .filter((selection) => selection.sessionIds.length > 0 && item.childIndexes.includes(selection.childIndexes[0])),
        })),
        notes: form.notes || undefined,
      });
      navigate('/bookings');
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not create booking');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell title="Bookings" subtitle="Create a booking on behalf of a parent">
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-semibold">Create booking</h2>
          <Link to="/bookings" className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Back to bookings</Link>
        </div>
        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        <form onSubmit={onSubmit} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm text-slate-600">Activity
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
                value={form.eventId}
                onChange={(e) => setForm((value) => ({
                  ...value,
                  eventId: e.target.value,
                  tickets: [createTicketRow()],
                  addOnSelections: {},
                }))}
                required
              >
                <option value="">Choose activity</option>
                {events.map((event) => (
                  <option key={event._id} value={event._id}>{event.title}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-slate-600">Parent name
              <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.parentName} onChange={(e) => setForm((value) => ({ ...value, parentName: e.target.value }))} required />
            </label>
            <label className="text-sm text-slate-600">Parent email
              <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" type="email" value={form.parentEmail} onChange={(e) => setForm((value) => ({ ...value, parentEmail: e.target.value }))} required />
            </label>
            <label className="text-sm text-slate-600">Parent phone
              <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.parentPhone} onChange={(e) => setForm((value) => ({ ...value, parentPhone: e.target.value }))} />
            </label>
          </div>

          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-slate-800">Attendees</h3>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-100"
                onClick={() => setForm((value) => ({
                  ...value,
                  children: [...value.children, { firstName: '', lastName: '', dateOfBirth: '' }],
                }))}
              >
                Add attendee
              </button>
            </div>
            {form.children.map((child, index) => (
              <div key={index} className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_180px_auto]">
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="First name"
                  value={child.firstName}
                  onChange={(e) => setForm((value) => ({
                    ...value,
                    children: value.children.map((entry, childIndex) => childIndex === index ? { ...entry, firstName: e.target.value } : entry),
                  }))}
                  required
                />
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Last name"
                  value={child.lastName}
                  onChange={(e) => setForm((value) => ({
                    ...value,
                    children: value.children.map((entry, childIndex) => childIndex === index ? { ...entry, lastName: e.target.value } : entry),
                  }))}
                  required
                />
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  type="date"
                  value={child.dateOfBirth}
                  onChange={(e) => setForm((value) => ({
                    ...value,
                    children: value.children.map((entry, childIndex) => childIndex === index ? { ...entry, dateOfBirth: e.target.value } : entry),
                  }))}
                  required
                />
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs hover:bg-slate-100 disabled:opacity-50"
                  disabled={form.children.length === 1}
                  onClick={() => setForm((value) => {
                    const nextTickets = value.tickets.map((ticket) => {
                      const remapped: Record<number, string[]> = {};
                      Object.entries(ticket.childSessionIds).forEach(([childIndex, sessionIds]) => {
                        const parsed = Number(childIndex);
                        if (parsed === index) return;
                        remapped[parsed > index ? parsed - 1 : parsed] = sessionIds;
                      });
                      return { ...ticket, childSessionIds: remapped };
                    });
                    return {
                      ...value,
                      children: value.children.filter((_, childIndex) => childIndex !== index),
                      tickets: nextTickets,
                      addOnSelections: Object.fromEntries(
                        Object.entries(value.addOnSelections).map(([addOnId, childSelections]) => {
                          const remapped: Record<number, string[]> = {};
                          Object.entries(childSelections).forEach(([childIndex, sessionIds]) => {
                            const parsed = Number(childIndex);
                            if (parsed === index) return;
                            remapped[parsed > index ? parsed - 1 : parsed] = sessionIds;
                          });
                          return [addOnId, remapped];
                        }),
                      ),
                    };
                  })}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-slate-800">Tickets and sessions</h3>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-100 disabled:opacity-50"
                disabled={!ticketTypes.length}
                onClick={() => setForm((value) => ({
                  ...value,
                  tickets: [...value.tickets, createTicketRow(ticketTypes[0]?.id || '')],
                }))}
              >
                Add ticket
              </button>
            </div>
            {form.tickets.map((ticket) => {
              const ticketType = ticketTypeById.get(ticket.ticketTypeId);
              const sessions = getSessionsForTicket(ticketType);
              return (
                <div key={ticket.id} className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                    <label className="text-sm text-slate-600">Ticket
                      <select
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
                        value={ticket.ticketTypeId}
                        onChange={(e) => setForm((value) => ({
                          ...value,
                          tickets: value.tickets.map((entry) => entry.id === ticket.id ? { ...entry, ticketTypeId: e.target.value, sessionIds: [], childSessionIds: {} } : entry),
                        }))}
                        required
                        disabled={!ticketTypes.length}
                      >
                        <option value="">Choose ticket</option>
                        {ticketTypes.map((entry) => (
                          <option key={entry.id} value={entry.id}>{renderTicketLabel(entry)}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className="self-end rounded-lg border border-slate-300 px-3 py-2 text-xs hover:bg-slate-100 disabled:opacity-50"
                      disabled={form.tickets.length === 1}
                      onClick={() => setForm((value) => ({
                        ...value,
                        tickets: value.tickets.filter((entry) => entry.id !== ticket.id),
                      }))}
                    >
                      Remove ticket
                    </button>
                  </div>
                  <div className="mt-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Sessions</p>
                    <div className="mt-2 grid gap-2 md:grid-cols-2">
                      {sessions.map((session) => {
                        const checked = ticket.sessionIds.includes(session.id);
                        return (
                          <label key={session.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => setForm((value) => ({
                                ...value,
                                tickets: value.tickets.map((entry) => {
                                  if (entry.id !== ticket.id) return entry;
                                  return {
                                    ...entry,
                                    sessionIds: e.target.checked
                                      ? Array.from(new Set([...entry.sessionIds, session.id]))
                                      : entry.sessionIds.filter((sessionId) => sessionId !== session.id),
                                    childSessionIds: Object.fromEntries(
                                      Object.entries(entry.childSessionIds).map(([childIndex, sessionIds]) => [
                                        childIndex,
                                        e.target.checked
                                          ? Array.from(new Set([...sessionIds, session.id]))
                                          : sessionIds.filter((sessionId) => sessionId !== session.id),
                                      ]),
                                    ),
                                  };
                                }),
                              }))}
                            />
                            <span>{formatSessionLabel(session)}</span>
                          </label>
                        );
                      })}
                    </div>
                    {ticketType && sessions.length > 0 && (
                      <button
                        type="button"
                        className="mt-2 text-xs font-medium text-[#1F8F5F] hover:underline"
                        onClick={() => setForm((value) => ({
                          ...value,
                          tickets: value.tickets.map((entry) => entry.id === ticket.id ? { ...entry, sessionIds: sessions.map((session) => session.id), childSessionIds: {} } : entry),
                        }))}
                      >
                        Select all sessions
                      </button>
                    )}
                    {!!ticket.sessionIds.length && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Attendees for this ticket</p>
                        {form.children.map((child, childIndex) => {
                          const selectedForChild = ticket.childSessionIds[childIndex] || ticket.sessionIds;
                          const childName = [child.firstName, child.lastName].map((part) => part.trim()).filter(Boolean).join(' ') || `Attendee ${childIndex + 1}`;
                          return (
                            <div key={`${ticket.id}-${childIndex}`} className="rounded-md border border-slate-200 bg-slate-50 p-2">
                              <p className="mb-1 text-xs font-medium text-slate-600">{childName}</p>
                              <div className="grid gap-1 md:grid-cols-2">
                                {ticket.sessionIds.map((sessionId) => {
                                  const session = eventSessions.find((entry) => entry.id === sessionId);
                                  return (
                                    <label key={`${ticket.id}-${childIndex}-${sessionId}`} className="flex items-center gap-2 text-sm text-slate-700">
                                      <input
                                        type="checkbox"
                                        checked={selectedForChild.includes(sessionId)}
                                        onChange={(e) => setForm((value) => ({
                                          ...value,
                                          tickets: value.tickets.map((entry) => {
                                            if (entry.id !== ticket.id) return entry;
                                            const current = entry.childSessionIds[childIndex] || entry.sessionIds;
                                            const next = e.target.checked
                                              ? Array.from(new Set([...current, sessionId]))
                                              : current.filter((id) => id !== sessionId);
                                            return {
                                              ...entry,
                                              childSessionIds: {
                                                ...entry.childSessionIds,
                                                [childIndex]: next,
                                              },
                                            };
                                          }),
                                        }))}
                                      />
                                      <span>{session ? formatSessionLabel(session) : sessionId}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {!!selectedEvent?.addOns?.length && Object.keys(selectedSessionIdsByChild).length > 0 && (
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-sm font-semibold text-slate-800">Add-ons</h3>
              {selectedEvent.addOns.map((addOn) => (
                <div key={addOn.id} className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-sm font-medium text-slate-900">
                    {addOn.name} (+{renderPrice(addOn.priceCents)} / session / attendee)
                  </p>
                  <div className="mt-2 space-y-2">
                    {form.children.map((child, childIndex) => {
                      const sessionIdsForChild = selectedSessionIdsByChild[childIndex] || [];
                      if (!sessionIdsForChild.length) return null;
                      const childName = [child.firstName, child.lastName].map((part) => part.trim()).filter(Boolean).join(' ') || `Attendee ${childIndex + 1}`;
                      return (
                        <div key={`${addOn.id}-${childIndex}`} className="rounded-md border border-slate-200 bg-slate-50 p-2">
                          <p className="mb-1 text-xs font-medium text-slate-600">{childName}</p>
                          <div className="grid gap-1 md:grid-cols-2">
                            {sessionIdsForChild.map((sessionId) => {
                              const session = eventSessions.find((entry) => entry.id === sessionId);
                              const checked = (form.addOnSelections[addOn.id]?.[childIndex] || []).includes(sessionId);
                              return (
                                <label key={`${addOn.id}-${childIndex}-${sessionId}`} className="flex items-center gap-2 text-sm text-slate-700">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => setForm((value) => {
                                      const existingAddOn = value.addOnSelections[addOn.id] || {};
                                      const currentForChild = existingAddOn[childIndex] || [];
                                      const nextForChild = e.target.checked
                                        ? Array.from(new Set([...currentForChild, sessionId]))
                                        : currentForChild.filter((id) => id !== sessionId);
                                      return {
                                        ...value,
                                        addOnSelections: {
                                          ...value.addOnSelections,
                                          [addOn.id]: {
                                            ...existingAddOn,
                                            [childIndex]: nextForChild,
                                          },
                                        },
                                      };
                                    })}
                                  />
                                  <span>{session ? formatSessionLabel(session) : sessionId}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          <label className="block text-sm text-slate-600">Notes
            <textarea className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" rows={3} value={form.notes} onChange={(e) => setForm((value) => ({ ...value, notes: e.target.value }))} />
          </label>
          <button className="rounded-lg bg-[#1F8F5F] px-5 py-2.5 font-medium text-white hover:bg-[#1F8F5F] disabled:opacity-60" disabled={saving}>{saving ? 'Creating...' : 'Create booking'}</button>
        </form>
      </section>
    </AdminShell>
  );
};

export default AttendeeCreatePage;
